import * as React from "react";
import styled, { css } from "react-emotion";
import Link from "next/link";
import { helvetica, small, strong } from "../theme/typefaces";
import * as bp from "../theme/layout/breakpoints";
import * as sp from "../theme/layout/spacing";
import { primaryColors, zuerichBlueColors, greyColors } from "../theme/colors";
import { Icon } from "../theme/icons/index";
/** Text Input */
export const labelStyle = css `
  ${helvetica};
  position: absolute;
  z-index: 1;
  padding: 0 0.25rem;
  top: -8px;
  left: 12px;
  width: auto;
  max-width: 100%;
  display: block;
  line-height: 1.5;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  font-size: 0.8125rem;
  background: ${primaryColors.white};
  color: ${greyColors["600"]};
  pointer-events: none;
  transition: color ease 0.3s;
`;
const LabelContainer = styled("div") `
  position: relative;
  &:hover,
  :focus {
    .${labelStyle} {
      color: ${primaryColors.zuerichBlue};
    }
  }
`;
export const Input = styled("input") `
  ${small};
  ${sp.pl2};
  ${sp.pv0};
  ${sp.ma0};
  padding-right: 40px;
  font-size: 16px; /* Needs to be at least 16px to prevent zoom on mobile */
  box-sizing: border-box;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  color: ${primaryColors.zuerichBlue};
  background: ${primaryColors.white};
  border: 1px solid ${greyColors["200"]};
  border-radius: 3px;
  -webkit-appearance: none;
  width: 100%;
  line-height: 40px;
  height: 40px;
  transition: border ease 0.3s;
  &:hover {
    border: 1px solid ${primaryColors.zuerichBlue};
  }
  &::placeholder {
    color: ${greyColors["600"]};
  }
`;
const InputIconContainer = styled("div") `
  position: absolute;
  pointer-events: none;
  top: 0;
  right: 0;
  ${sp.ph2};
  line-height: 42px;
  color: ${greyColors["600"]};
`;
export const InputField = ({ label, placeholder, icon, ...inputProps }) => (<LabelContainer>
    {label && <label className={labelStyle}>{label}</label>}
    <Input type="text" placeholder={placeholder} {...inputProps}/>
    {icon && (<InputIconContainer>
        <Icon name={icon}/>
      </InputIconContainer>)}
  </LabelContainer>);
/** Select menu */
export const Select = styled("select") `
  min-width: 200px;
  width: 100%;
  margin: 0;
  ${small};
  ${sp.pl2};
  padding-right: 40px;
  color: ${primaryColors.zuerichBlue};
  border: 1px solid ${greyColors["200"]};
  border-radius: 3px;
  appearance: none;
  line-height: 40px;
  height: 40px;
  font-size: 16px;
  -webkit-appearance: none;
  background: ${primaryColors.white};
  transition: border-color ease 0.3s;
  &:hover,
  &:focus {
    cursor: pointer;
    border-color: ${primaryColors.zuerichBlue};
  }
  &::-ms-expand {
    display: none;
  }
  outline: 0;
`;
export const SelectField = ({ label, icon, selected, options, onChange, ...selectProps }) => (<LabelContainer>
    {label && <label className={labelStyle}>{label}</label>}
    <Select value={selected} onChange={onChange} {...selectProps}>
      {options.map((opt) => (<option value={opt.id ? opt.id : opt} key={opt.id ? opt.id : opt}>
          {`${opt.label ? opt.label : opt}`}
        </option>))}
    </Select>
    <InputIconContainer>
      <Icon name={icon}/>
    </InputIconContainer>
  </LabelContainer>);
/** Tabs */
export const tabsContainerStyle = css `
  label: tabsContainerStyle;
  width: 100%;
  display: flex;
  justify-content: space-around;
  flex-wrap: nowrap;
  border-top: 1px solid ${greyColors["200"]};
  border-bottom: 1px solid ${greyColors["200"]};
  background-color: ${primaryColors.white};
  ${bp.small `
    justify-content: flex-start;
  `};
`;
export const tabStyle = css `
  label: tab;
  ${sp.ma0};
  ${sp.pa0};
  ${small};
  ${strong};
  box-sizing: border-box;
  cursor: pointer;
  outline: none;
  border: 0;
  flex-grow: 1;
  text-align: center;
  line-height: 36px;
  background: transparent;
  color: ${primaryColors.zuerichBlue};
  cursor: pointer;
  transition: color ease 0.3s;
  &:hover {
    color: ${primaryColors.darkGrey};
    cursor: pointer;
  }
  &:focus {
    color: ${primaryColors.darkGrey};
    border-bottom: 2px solid ${greyColors["200"]};
    margin-top: 2px;
  }
  ${bp.small `
    flex-grow: 0;
    ${sp.ph3};
  `};
`;
export const tabActiveStyle = css `
  label: tabActive;
  ${tabStyle};
  color: ${primaryColors.darkGrey};
  border-bottom: 2px solid ${greyColors["200"]};
  margin-top: 2px;
`;
export const Tab = ({ text, active, onTabChange, tabIndex }) => (<button className={active ? tabActiveStyle : tabStyle} onClick={(e) => onTabChange(e)} value={text} role="tab" id={`tab-${text}`} aria-selected={active ? "true" : "false"} aria-expanded={active ? "true" : "false"} tab-index={tabIndex} aria-controls={`panel-${text}`}>
    {text}
  </button>);
export const Tabs = ({ tabs, selected, onTabChange }) => (<div className={tabsContainerStyle} role="tablist">
    {tabs.map((t, i) => (<Tab key={t} active={t === selected ? true : false} text={t} onTabChange={() => onTabChange({ view: t })} tabIndex={i}/>))}
  </div>);
export const crumbLinkAnchorStyle = css `
  ${small};
  color: ${primaryColors.zuerichBlue};
  cursor: pointer;
  display: flex;
  align-items: center;
  text-decoration: none;
  & p {
    ${sp.ma0};
    transition: box-shadow ease 0.3s;
    text-decoration: none;
  }
  & svg {
    font-size: 1.8em;
    display: block;
    bottom: 0;
    ${sp.ma0};
  }
  &:hover {
    cursor: pointer;
    color: ${primaryColors.zuerichBlue};
    p {
      box-shadow: ${primaryColors.zuerichBlue} 0px 1px 0px 0px;
    }
  }
`;
export const withPaddingRightStyle = css `
  ${sp.pr1};
`;
export const withPaddingLeftStyle = css `
  ${sp.pl1};
`;
export const Crumb = ({ icon = "ChevronLeft", label = "Zurück", rightAligned = false, href, ...linkAnchorProps }) => (<Link href={href}>
    {rightAligned ? (<a className={crumbLinkAnchorStyle} style={{ justifyContent: "flex-end" }} {...linkAnchorProps}>
        <p>{label}</p>
        <Icon className={withPaddingLeftStyle} name={icon}/>
      </a>) : (<a className={crumbLinkAnchorStyle} style={{ justifyContent: "flex-start" }} {...linkAnchorProps}>
        <Icon className={withPaddingRightStyle} name={icon}/>
        <p>{label}</p>
      </a>)}
  </Link>);
/** Combos */
export const comboStyle = css `
  ${small};
  color: ${primaryColors.zuerichBlue};
  &:hover {
    color: ${primaryColors.zuerichBlue};
  }
`;
const comboContentStyle = css `
  display: flex;
  align-items: center;
  & p {
    ${sp.ma0};
    ${sp.pr1};
  }
  & svg {
    display: block;
    ${sp.ma0};
    bottom: 0;
  }
`;
export const Combo = ({ icon, text, rightAligned }) => (<div className={comboStyle}>
    {rightAligned ? (<div className={comboContentStyle} style={{ justifyContent: "flex-end" }}>
        <p className={withPaddingRightStyle}>{text}</p>
        <Icon name={icon}/>
      </div>) : (<div className={comboContentStyle} style={{ justifyContent: "flex-start" }}>
        <Icon name={icon}/>
        <p className={withPaddingLeftStyle}>{text}</p>
      </div>)}
  </div>);
export const ThemenCombo = ({ icon = "Folder", text = "Themen", rightAligned = false }) => <Combo icon={icon} text={text} rightAligned={rightAligned}/>;
export const DimensionCombo = ({ icon = "Tag", text = "Dimensionen", rightAligned = false }) => <Combo icon={icon} text={text} rightAligned={rightAligned}/>;
export const LocationCombo = ({ icon = "Location", text = "Raum", rightAligned = false }) => <Combo icon={icon} text={text} rightAligned={rightAligned}/>;
export const TimeCombo = ({ icon = "Time", text = "Zeit", rightAligned = false }) => <Combo icon={icon} text={text} rightAligned={rightAligned}/>;
/** Primary button */
const ButtonIconContainer = styled("div") `
  & svg {
    flex-shrink: 0;
    font-size: 1.2em;
    ${sp.ml1};
    display: block;
    bottom: 0;
  }
`;
export const buttonStyle = css `
  ${small};
  ${strong};
  padding: 0;
  box-sizing: border-box;
  cursor: pointer;
  width: 100%;
  outline: none;
  line-height: 40px;
  font-size: 16px;
  text-align: left;
  ${sp.ph1};
  border: 0;
  border-radius: 3px;
  color: ${primaryColors.white};
  background-color: ${primaryColors.zuerichBlue};
  display: flex;
  align-items: center;
  border: 1px solid ${primaryColors.zuerichBlue};
  text-decoration: none;
  transition: color ease 0.3s, background-color ease 0.3s, border ease 0.3s;
  & span {
    overflow: hidden;
    white-space: nowrap;
  }
  &:hover {
    color: ${primaryColors.zuerichBlue};
    background-color: ${primaryColors.white};
  }
`;
export const Button = ({ text, icon, href, as, ...linkProps }) => (<Link href={href} as={as} passHref>
    {icon ? (<a className={buttonStyle} style={{ justifyContent: "space-between" }} {...linkProps}>
        <span>{text}</span>
        <ButtonIconContainer>
          <Icon name={icon}/>
        </ButtonIconContainer>
      </a>) : (<a className={buttonStyle} style={{ justifyContent: "center" }} {...linkProps}>
        <span>{text}</span>
      </a>)}
  </Link>);
/** Secondary button */
export const secondaryButtonStyle = css `
  ${buttonStyle};
  ${helvetica};
  color: ${primaryColors.zuerichBlue};
  background-color: ${primaryColors.white};
  border: 1px solid ${zuerichBlueColors["500"]};
  &:hover {
    color: ${primaryColors.white};
    background-color: ${primaryColors.zuerichBlue};
    border: 1px solid ${primaryColors.zuerichBlue};
  }
`;
export const SecondaryButton = ({ text, href, icon, ...secondaryButtonProps }) => (<Link href={href}>
    {icon ? (<button className={secondaryButtonStyle} style={{ justifyContent: "space-between" }} {...secondaryButtonProps}>
        <span>{text}</span>
        <ButtonIconContainer>
          <Icon name={icon}/>
        </ButtonIconContainer>
      </button>) : (<button className={secondaryButtonStyle} style={{ justifyContent: "center" }} {...secondaryButtonProps}>
        <span>{text}</span>
      </button>)}
  </Link>);
export const LinkButtonAnchor = styled("a") `
  ${small};
  cursor: pointer;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  line-height: 40px;
  font-size: 14px;
  ${sp.ph2};
  ${sp.mb1};
  border: 0;
  outline: none;
  border-radius: 3px;
  text-decoration: none;
  box-sizing: border-box;
  color: ${primaryColors.darkGrey};
  background-color: ${greyColors["50"]};
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background-color ease 0.3s;
  & span {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
  & svg {
    color: ${primaryColors.zuerichBlue};
  }
  &:hover,
  :focus {
    background-color: ${greyColors["100"]};
  }
`;
export const LinkButton = ({ text, icon, ...linkAnchorProps }) => (<LinkButtonAnchor {...linkAnchorProps}>
    <span>{text}</span>
    <ButtonIconContainer>
      <Icon name={icon}/>
    </ButtonIconContainer>
  </LinkButtonAnchor>);



// WEBPACK FOOTER //
// ./src/components/Controls.tsx